import React, { useState } from 'react';
import { MessageCircle, X, Send, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState(1); // 1: initial, 2: form, 3: chat

  const quickQuestions = [
    "Suis-je éligible aux aides ?",
    "Quel est le montant des aides ?",
    "Combien de temps pour l'installation ?",
    "Comment faire ma demande ?"
  ];

  const handleQuickQuestion = (question) => {
    toast.info('Un conseiller va vous rappeler sous 5 minutes pour répondre à votre question.');
    setIsOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (step === 2) {
      // Save contact info and move to chat
      setStep(3);
      toast.success('Merci ! Un conseiller va vous répondre dans quelques instants.');
      
      // Simulate sending to backend
      console.log('Chat contact:', { name, phone, initialMessage: message });
      
      // GTM event
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'chat_widget_opened',
          chat_type: 'live_support'
        });
      }
    } else if (step === 3) {
      // Send message in chat
      if (message.trim()) {
        toast.success('Message envoyé ! Réponse dans quelques instants...');
        setMessage('');
      }
    }
  };

  const handleCallRequest = () => {
    toast.success('Demande enregistrée ! Nous vous rappelons sous 5 minutes.');
    setIsOpen(false);
    
    // Save call request
    console.log('Call request:', { name, phone });
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-40 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full p-4 shadow-2xl hover:shadow-blue-500/50 hover:scale-110 transition-all duration-300 flex items-center gap-2 group animate-bounce"
          aria-label="Ouvrir le chat"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="hidden md:inline-block font-semibold pr-2 max-w-0 group-hover:max-w-xs transition-all duration-300 overflow-hidden whitespace-nowrap">
            Besoin d'aide ?
          </span>
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-3xl shadow-2xl border-2 border-blue-600 flex flex-col max-h-[600px] animate-in slide-in-from-bottom duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-3xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <MessageCircle className="h-6 w-6 text-blue-600" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h3 className="font-bold text-lg">Assistance en direct</h3>
                <p className="text-xs text-blue-100">En ligne • Réponse sous 2 min</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
            {step === 1 && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                  <p className="text-gray-800 font-medium mb-3">
                    👋 Bonjour ! Comment puis-je vous aider aujourd'hui ?
                  </p>
                  <p className="text-sm text-gray-600">
                    Cliquez sur une question ou écrivez la vôtre :
                  </p>
                </div>

                <div className="space-y-2">
                  {quickQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickQuestion(question)}
                      className="w-full text-left bg-white hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-xl p-3 transition-all duration-200 text-sm text-gray-700 hover:text-blue-700 font-medium"
                    >
                      {question}
                    </button>
                  ))}
                </div>

                <Button
                  onClick={() => setStep(2)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-semibold rounded-xl"
                >
                  Poser une autre question
                </Button>

                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-3">Ou appelez-nous directement :</p>
                  <a
                    href="tel:+33970707069"
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                  >
                    <Phone className="h-5 w-5" />
                    09.70.70.70.69
                  </a>
                </div>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                  <p className="text-gray-800 font-medium mb-2">
                    Pour mieux vous aider, laissez-nous vos coordonnées :
                  </p>
                </div>

                <div>
                  <Input
                    type="text"
                    placeholder="Votre nom"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="text-lg py-6"
                  />
                </div>

                <div>
                  <Input
                    type="tel"
                    placeholder="Votre téléphone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    pattern="[0-9]{10}"
                    className="text-lg py-6"
                  />
                </div>

                <div>
                  <Textarea
                    placeholder="Votre question..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    rows={3}
                    className="text-base resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-semibold rounded-xl"
                >
                  Envoyer ma question
                  <Send className="ml-2 h-5 w-5" />
                </Button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-gray-600 hover:text-gray-800 text-sm py-2"
                >
                  ← Retour
                </button>
              </form>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>{name}</strong>
                  </p>
                  <p className="text-gray-600">{message}</p>
                </div>

                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                  <p className="text-gray-800 font-medium mb-2">
                    ✅ Message bien reçu !
                  </p>
                  <p className="text-sm text-gray-600 mb-3">
                    Un de nos conseillers va vous répondre dans quelques instants.
                  </p>
                  <p className="text-sm text-gray-600">
                    En attendant, vous pouvez aussi nous appeler directement :
                  </p>
                </div>

                <Button
                  onClick={handleCallRequest}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-base font-semibold rounded-xl"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Me faire rappeler maintenant
                </Button>

                <button
                  onClick={() => {
                    setStep(1);
                    setMessage('');
                  }}
                  className="w-full text-gray-600 hover:text-gray-800 text-sm py-2"
                >
                  Poser une autre question
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 bg-gray-100 rounded-b-3xl border-t">
            <p className="text-xs text-gray-500 text-center">
              Réponse sous 2 minutes • Service gratuit
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;